import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";

// Critical pages - loaded immediately
import HomePage from "./pages/HomePage";
import CarDetailPage from "./pages/CarDetailPage";

// Lazy loaded pages - not needed on initial load
const SimulasiKreditM6Page = lazy(() => import("./pages/SimulasiKreditM6Page"));
const ArticleListPage = lazy(() => import("./pages/ArticleListPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));

// Components
import Navbar from "./components/Navbar";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import SchemaMarkup from "./components/SchemaMarkup";
import GoogleAnalytics, { trackPageView } from "./components/GoogleAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  }
});

// Component to track page views on route change
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
}

function App() {
  const [settings, setSettings] = useState(() => {
    // Try to get cached settings from localStorage for instant display
    try {
      const cached = localStorage.getItem('byd_admin_settings');
      if (cached) {
        const data = JSON.parse(cached);
        // Set title immediately from cache
        if (data.site_title) document.title = data.site_title;
        return data;
      }
    } catch {}
    return null;
  });
  const [salesInfo, setSalesInfo] = useState(null);
  const [cars, setCars] = useState(() => {
    // Try to get cached cars from localStorage
    try {
      const cached = localStorage.getItem('byd_cars');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [pageContents, setPageContents] = useState({});

  // Remove "Made with Emergent" watermark from DOM - AGGRESSIVE approach
  useEffect(() => {
    const removeWatermark = () => {
      // Target the specific badge by ID first (most efficient)
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
      
      // Remove by various selectors
      const selectors = [
        '#emergent-badge',
        'a[href*="emergent"]',
        'a[href*="app.emergent.sh"]',
        '[class*="emergent"]',
        '[id*="emergent"]'
      ];
      
      selectors.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => el.remove());
        } catch (e) {}
      });
    };

    // Run immediately
    removeWatermark();

    // Set up MutationObserver to catch dynamically injected elements
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if this is the badge or contains emergent
            if (node.id === 'emergent-badge' || 
                node.href?.includes('emergent') ||
                node.className?.toString?.().includes('emergent')) {
              node.remove();
              continue;
            }
            // Check children
            const badge = node.querySelector?.('#emergent-badge, a[href*="emergent"]');
            if (badge) badge.remove();
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    // Run at multiple intervals to catch late injections
    const intervals = [0, 10, 20, 50, 100, 200, 300, 500, 1000, 2000];
    intervals.forEach(ms => setTimeout(removeWatermark, ms));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await api.post('/seed-data').catch(() => {});
        const [settingsRes, salesRes, carsRes, simulasiKreditRes] = await Promise.all([
          api.get('/admin-settings'),
          api.get('/sales-info'),
          api.get('/cars'),
          api.get('/page-content/simulasi-kredit').catch(() => ({ data: {} }))
        ]);
        setSettings(settingsRes.data);
        setSalesInfo(salesRes.data);
        setCars(carsRes.data);
        setPageContents({ 'simulasi-kredit': simulasiKreditRes.data });
        // Cache for instant display on next load
        try {
          localStorage.setItem('byd_admin_settings', JSON.stringify(settingsRes.data));
          localStorage.setItem('byd_cars', JSON.stringify(carsRes.data));
        } catch {}
      } catch (e) {
        console.error('Error:', e);
      }
    };
    fetchData();
  }, []);

  // Update browser tab title from admin settings
  useEffect(() => {
    if (settings?.site_title) {
      document.title = settings.site_title;
    }
  }, [settings]);

  const location = window.location.pathname;
  const isAdminPage = location === '/admin';

  return (
    <div className="App min-h-screen bg-white">
      <BrowserRouter>
        <GoogleAnalytics 
          gaId={settings?.google_analytics_id} 
          enabled={settings?.google_analytics_enabled} 
        />
        <PageViewTracker />
        <SchemaMarkup salesInfo={salesInfo} />
        <AppContent settings={settings} cars={cars} salesInfo={salesInfo} pageContents={pageContents} />
      </BrowserRouter>
    </div>
  );
}

function AppContent({ settings, cars, salesInfo, pageContents }) {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';

  // Loading fallback for lazy loaded pages
  const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <>
      {/* Skip Link for Accessibility */}
      {!isAdminPage && (
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-slate-900 focus:shadow-lg"
        >
          Langsung ke konten utama
        </a>
      )}
      {!isAdminPage && <Navbar settings={settings} cars={cars} pageContents={pageContents} />}
      <main id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/mobil/:slug" element={<CarDetailPage />} />
            <Route path="/simulasi-kredit" element={<SimulasiKreditM6Page />} />
            <Route path="/simulasi-kredit-byd-m6-jakarta" element={<SimulasiKreditM6Page />} />
            <Route path="/artikel" element={<ArticleListPage />} />
            <Route path="/artikel/:slug" element={<ArticleDetailPage />} />
            <Route path="/kebijakan-privasi" element={<PrivacyPolicyPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminPage && <FloatingWhatsApp settings={settings} salesInfo={salesInfo} />}
    </>
  );
}

export default App;
